import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { 
  Play, ArrowLeft, Users, Video, Trash2, 
  Eye, Calendar, Building2, Mail, Crown,
  AlertTriangle, RefreshCw, TrendingUp, CheckCircle,
  XCircle, Clock, CreditCard, BarChart3, Activity
} from "lucide-react";

import { API_URL, BASE_URL } from "../config/api";

const API = API_URL;

const AdminDashboard = () => {
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [videoModal, setVideoModal] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [projectsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/projects`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() })
      ]);
      setProjects(projectsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Kon data niet laden");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!user?.is_admin) {
      toast.error("Geen toegang - alleen voor admins");
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [user, navigate, fetchData]);

  // Calculate statistics
  const stats = {
    totalUsers: users.length,
    demoUsers: users.filter(u => u.subscription_plan === "demo").length,
    basicUsers: users.filter(u => u.subscription_plan === "basic").length,
    proUsers: users.filter(u => u.subscription_plan === "pro").length,
    adminUsers: users.filter(u => u.subscription_plan === "admin").length,
    paidUsers: users.filter(u => ["basic", "pro"].includes(u.subscription_plan)).length,
    
    totalProjects: projects.length,
    completedVideos: projects.filter(p => p.status === "completed").length,
    failedVideos: projects.filter(p => p.status === "failed").length,
    generatingVideos: projects.filter(p => p.status === "generating").length,
    draftProjects: projects.filter(p => p.status === "draft").length,
    
    // Videos this month
    videosThisMonth: projects.filter(p => {
      const created = new Date(p.created_at);
      const now = new Date();
      return created.getMonth() === now.getMonth() && created.getFullYear() === now.getFullYear();
    }).length,
    
    // Total videos generated (all users combined)
    totalVideosGenerated: users.reduce((sum, u) => sum + (u.videos_generated || 0), 0),
    
    // Revenue estimate (basic €25, pro €50)
    estimatedMRR: (users.filter(u => u.subscription_plan === "basic").length * 25) + 
                  (users.filter(u => u.subscription_plan === "pro").length * 50)
  };

  const handleDeleteProject = async (projectId) => {
    if (!confirm("Weet je zeker dat je dit project wilt verwijderen?")) return;
    
    setDeleting(projectId);
    try {
      await axios.delete(`${API}/admin/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Project verwijderd");
      setProjects(projects.filter(p => p.id !== projectId));
    } catch (error) {
      toast.error("Kon project niet verwijderen");
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("nl-NL", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getPlanBadge = (plan) => {
    const colors = {
      demo: "bg-slate-500/20 text-slate-400",
      basic: "bg-blue-500/20 text-blue-400",
      pro: "bg-purple-500/20 text-purple-400",
      admin: "bg-amber-500/20 text-amber-400"
    };
    return colors[plan] || colors.demo;
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: "bg-slate-500/20 text-slate-400",
      generating: "bg-amber-500/20 text-amber-400",
      completed: "bg-emerald-500/20 text-emerald-400",
      failed: "bg-red-500/20 text-red-400"
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 dark-mode">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Terug naar Dashboard</span>
          </Link>
          <div className="flex items-center gap-4">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
              <Crown className="w-3 h-3 mr-1" />
              Admin
            </Badge>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                <Play className="w-4 h-4 text-white fill-white" />
              </div>
              <span className="font-semibold text-white">VideoMaker</span>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Admin Dashboard
              </h1>
              <p className="text-slate-400">Beheer alle projecten en gebruikers</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={async () => {
                  try {
                    toast.info("Video migratie gestart...");
                    await axios.post(`${API}/admin/fix-all-videos`, {}, {
                      headers: getAuthHeaders()
                    });
                    toast.success("Video migratie gestart! Check de server logs voor voortgang.");
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Kon migratie niet starten");
                  }
                }}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="fix-all-videos-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Fix Alle Videos
              </Button>
              <Button
                onClick={fetchData}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Vernieuwen
              </Button>
            </div>
          </div>

          {/* Stats Grid - Row 1: Main Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center">
                  <Users className="w-6 h-6 text-indigo-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Totaal Gebruikers</p>
                  <p className="text-2xl font-bold text-white">{stats.totalUsers}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <Video className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Video's Gemaakt</p>
                  <p className="text-2xl font-bold text-white">{stats.completedVideos}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Betaalde Abonnementen</p>
                  <p className="text-2xl font-bold text-white">{stats.paidUsers}</p>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-slate-400 text-sm">Geschatte MRR</p>
                  <p className="text-2xl font-bold text-white">€{stats.estimatedMRR}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Stats Grid - Row 2: Subscription Breakdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-sm">Demo Gebruikers</span>
                  <Badge className="bg-slate-500/20 text-slate-400">Gratis</Badge>
                </div>
                <p className="text-xl font-bold text-white">{stats.demoUsers}</p>
                <p className="text-xs text-slate-500 mt-1">
                  {stats.totalUsers > 0 ? Math.round((stats.demoUsers / stats.totalUsers) * 100) : 0}% van totaal
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-sm">Basic Abonnementen</span>
                  <Badge className="bg-blue-500/20 text-blue-400">€25/m</Badge>
                </div>
                <p className="text-xl font-bold text-white">{stats.basicUsers}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Omzet: €{stats.basicUsers * 25}/maand
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-sm">Pro Abonnementen</span>
                  <Badge className="bg-purple-500/20 text-purple-400">€50/m</Badge>
                </div>
                <p className="text-xl font-bold text-white">{stats.proUsers}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Omzet: €{stats.proUsers * 50}/maand
                </p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400 text-sm">Admin Accounts</span>
                  <Badge className="bg-amber-500/20 text-amber-400">Admin</Badge>
                </div>
                <p className="text-xl font-bold text-white">{stats.adminUsers}</p>
                <p className="text-xs text-slate-500 mt-1">
                  Onbeperkt toegang
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Stats Grid - Row 3: Video Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 text-center">
                <CheckCircle className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-lg font-bold text-white">{stats.completedVideos}</p>
                <p className="text-xs text-slate-400">Voltooid</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 text-center">
                <Clock className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-lg font-bold text-white">{stats.generatingVideos}</p>
                <p className="text-xs text-slate-400">Bezig</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 text-center">
                <XCircle className="w-8 h-8 text-red-400 mx-auto mb-2" />
                <p className="text-lg font-bold text-white">{stats.failedVideos}</p>
                <p className="text-xs text-slate-400">Mislukt</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 text-center">
                <Activity className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-lg font-bold text-white">{stats.videosThisMonth}</p>
                <p className="text-xs text-slate-400">Deze Maand</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/80 border-slate-800">
              <CardContent className="p-4 text-center">
                <BarChart3 className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <p className="text-lg font-bold text-white">{stats.totalProjects}</p>
                <p className="text-xs text-slate-400">Totaal Projecten</p>
              </CardContent>
            </Card>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            <Button
              variant={activeTab === "overview" ? "default" : "outline"}
              onClick={() => setActiveTab("overview")}
              className={activeTab === "overview" ? "bg-indigo-600" : "border-slate-700 text-slate-300"}
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Overzicht
            </Button>
            <Button
              variant={activeTab === "projects" ? "default" : "outline"}
              onClick={() => setActiveTab("projects")}
              className={activeTab === "projects" ? "bg-indigo-600" : "border-slate-700 text-slate-300"}
              data-testid="tab-projects"
            >
              <Video className="w-4 h-4 mr-2" />
              Projecten ({projects.length})
            </Button>
            <Button
              variant={activeTab === "users" ? "default" : "outline"}
              onClick={() => setActiveTab("users")}
              className={activeTab === "users" ? "bg-indigo-600" : "border-slate-700 text-slate-300"}
              data-testid="tab-users"
            >
              <Users className="w-4 h-4 mr-2" />
              Gebruikers ({users.length})
            </Button>
          </div>

          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Users */}
              <Card className="bg-slate-900/80 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-400" />
                    Recente Gebruikers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {users.slice(0, 5).map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                        <div>
                          <p className="text-white text-sm">{u.email}</p>
                          <p className="text-slate-400 text-xs">{u.company_name || "Geen bedrijf"}</p>
                        </div>
                        <Badge className={getPlanBadge(u.subscription_plan)}>
                          {u.subscription_plan}
                        </Badge>
                      </div>
                    ))}
                    {users.length === 0 && (
                      <p className="text-slate-400 text-center py-4">Geen gebruikers</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Videos */}
              <Card className="bg-slate-900/80 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Video className="w-5 h-5 text-emerald-400" />
                    Recente Video's
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {projects.slice(0, 5).map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                        <div>
                          <p className="text-white text-sm">{p.name}</p>
                          <p className="text-slate-400 text-xs">{p.user_email} • {formatDate(p.created_at)}</p>
                        </div>
                        <Badge className={getStatusBadge(p.status)}>
                          {p.status}
                        </Badge>
                      </div>
                    ))}
                    {projects.length === 0 && (
                      <p className="text-slate-400 text-center py-4">Geen projecten</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Subscription Summary */}
              <Card className="bg-slate-900/80 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-purple-400" />
                    Abonnementen Overzicht
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Demo (gratis)</span>
                      <span className="text-white font-bold">{stats.demoUsers}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-slate-500 h-2 rounded-full" 
                        style={{ width: `${stats.totalUsers > 0 ? (stats.demoUsers / stats.totalUsers) * 100 : 0}%` }}
                      />
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Basic (€25/m)</span>
                      <span className="text-white font-bold">{stats.basicUsers}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-blue-500 h-2 rounded-full" 
                        style={{ width: `${stats.totalUsers > 0 ? (stats.basicUsers / stats.totalUsers) * 100 : 0}%` }}
                      />
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400">Pro (€50/m)</span>
                      <span className="text-white font-bold">{stats.proUsers}</span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full" 
                        style={{ width: `${stats.totalUsers > 0 ? (stats.proUsers / stats.totalUsers) * 100 : 0}%` }}
                      />
                    </div>

                    <div className="pt-4 border-t border-slate-700">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-300 font-medium">Totale MRR</span>
                        <span className="text-green-400 text-xl font-bold">€{stats.estimatedMRR}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Video Performance */}
              <Card className="bg-slate-900/80 border-slate-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Activity className="w-5 h-5 text-blue-400" />
                    Video Prestaties
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                        Succesvol
                      </span>
                      <span className="text-emerald-400 font-bold">{stats.completedVideos}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 flex items-center gap-2">
                        <XCircle className="w-4 h-4 text-red-400" />
                        Mislukt
                      </span>
                      <span className="text-red-400 font-bold">{stats.failedVideos}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-slate-400 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-amber-400" />
                        In Progress
                      </span>
                      <span className="text-amber-400 font-bold">{stats.generatingVideos}</span>
                    </div>

                    <div className="pt-4 border-t border-slate-700">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-slate-300 font-medium">Success Rate</span>
                        <span className="text-emerald-400 font-bold">
                          {stats.totalProjects > 0 
                            ? Math.round((stats.completedVideos / (stats.completedVideos + stats.failedVideos || 1)) * 100) 
                            : 0}%
                        </span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-3">
                        <div 
                          className="bg-emerald-500 h-3 rounded-full" 
                          style={{ 
                            width: `${stats.totalProjects > 0 
                              ? (stats.completedVideos / (stats.completedVideos + stats.failedVideos || 1)) * 100 
                              : 0}%` 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === "projects" && (
            <div className="space-y-4">
              {projects.length === 0 ? (
                <Card className="bg-slate-900/50 border-slate-800">
                  <CardContent className="p-8 text-center">
                    <Video className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                    <p className="text-slate-400">Nog geen projecten</p>
                  </CardContent>
                </Card>
              ) : (
                projects.map((project) => (
                  <Card key={project.id} className="bg-slate-900/80 border-slate-800 hover:border-slate-700 transition-all">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-medium">{project.name}</h3>
                            <Badge className={getStatusBadge(project.status)}>
                              {project.status}
                            </Badge>
                            <Badge variant="outline" className="border-slate-700 text-slate-400">
                              {project.platform} • {project.duration}s
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {project.user_email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Building2 className="w-4 h-4" />
                              {project.company_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {formatDate(project.created_at)}
                            </span>
                          </div>
                          {project.text_prompt && (
                            <p className="mt-2 text-slate-500 text-sm truncate max-w-xl">
                              "{project.text_prompt}"
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {project.video_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-slate-700 text-slate-300"
                              onClick={() => setVideoModal({
                                url: `${BASE_URL}${project.video_url}`,
                                name: project.name
                              })}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            onClick={() => handleDeleteProject(project.id)}
                            disabled={deleting === project.id}
                            data-testid={`delete-project-${project.id}`}
                          >
                            {deleting === project.id ? (
                              <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          )}

          {/* Users Tab */}
          {activeTab === "users" && (
            <div className="space-y-4">
              {users.map((u) => (
                <Card key={u.id} className="bg-slate-900/80 border-slate-800 hover:border-slate-700 transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-white font-medium">{u.email}</h3>
                          <Badge className={getPlanBadge(u.subscription_plan)}>
                            {u.subscription_plan}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {u.company_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Video className="w-4 h-4" />
                            {u.project_count} projecten
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(u.created_at)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-slate-400 text-sm">Videos deze maand</p>
                        <p className="text-xl font-bold text-white">{u.videos_created_this_month}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Video Modal */}
      {videoModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setVideoModal(null)}
        >
          <div 
            className="bg-slate-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h3 className="text-white font-medium">{videoModal.name}</h3>
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-400 hover:text-white"
                onClick={() => setVideoModal(null)}
              >
                ✕
              </Button>
            </div>
            <div className="p-4">
              <video
                src={videoModal.url}
                controls
                autoPlay
                className="w-full rounded-lg"
                style={{ maxHeight: "70vh" }}
              >
                Je browser ondersteunt geen video playback.
              </video>
            </div>
            <div className="p-4 border-t border-slate-800 flex justify-end gap-2">
              <Button
                variant="outline"
                className="border-slate-700 text-slate-300"
                onClick={() => window.open(videoModal.url, '_blank')}
              >
                Open in nieuw tabblad
              </Button>
              <Button
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={() => {
                  const a = document.createElement('a');
                  a.href = videoModal.url;
                  a.download = `${videoModal.name}.mp4`;
                  a.click();
                }}
              >
                Download
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
